<template>
  <v-container style="height: 100%">
    <div class="d-flex justify-space-between">
      <h3>
        {{ $tc('vehicle_map')}}
      </h3>
      <div class="d-flex align-center">
        <span v-if="allCustomers" class="text-subtitle-1">{{ $t('all_customers') }}</span>
        <span v-else class="text-subtitle-1">{{ customerContext.name }}</span>
        <vf-tooltip :text="$t('toggle_all_customers')" bottom>
          <v-switch v-model="allCustomers" class="ml-3"/>
        </vf-tooltip>
      </div>
    </div>
    <gmap-map id="vehicle-map" ref="map" :zoom="12" :center="dummyCenter" v-if="vehiclesLoaded"/>
    <v-progress-linear v-else indeterminate class="mt-3"></v-progress-linear>
  </v-container>
</template>

<script>
import VfTooltip from '@/components/shared/VfTooltip'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { gmapApi } from 'vue2-google-maps'
import MarkerClusterer from 'marker-clusterer-plus'
import Moment from 'moment-timezone'
import { each, filter, get } from 'lodash'

const clusterStyles = [
  {
    url: '/img/map-marker-16.png',
    height: 16,
    width: 16
  },
  {
    url: '/img/map-marker-24.png',
    height: 24,
    width: 24
  },
  {
    url: '/img/map-marker-32.png',
    height: 32,
    width: 32
  }
]

export default {
  name: 'VehicleMap',
  components: {
    VfTooltip
  },
  mixins: [
    CurrentUserMixin
  ],
  data () {
    return {
      vehicles: [],
      vehiclesLoaded: false,
      allCustomers: this.$route.query.all === '1' || this.$route.query.all === 'true',
      // vue-google-maps complains if no center is defined. We'll set the true bounds once the vehicles are loaded
      dummyCenter: { lat: 0, lng: 0 }
    }
  },
  computed: {
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object inside Vue as we would normally in
     * any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi
  },
  methods: {
    /**
     * Initialize the component - will fetch vehicles then populate the map.
     */
    async initialize () {
      await this.fetchVehicles()
      // Initialize the Google Maps state once the map promise is resolved.
      this.$refs.map.$mapPromise.then((map) => {
        const bounds = new this.google.maps.LatLngBounds()
        const infoWindow = new this.google.maps.InfoWindow()
        const markers = []

        each(this.vehicles, vehicle => {
          const latlng = new this.google.maps.LatLng(vehicle.state.lat.value, vehicle.state.long.value)
          bounds.extend(latlng)

          const marker = new this.google.maps.Marker({
            position: latlng,
            title: vehicle.name,
            map: map,
            zIndex: 100,
            icon: '/img/map-measle.png'
          })

          let infoWindowContent = `<h5>${this.$tc('vehicle')}: <a href="/vehicle/${vehicle.id}">${vehicle.name}</a></h5>`
          infoWindowContent += `${this.$tc('last_update')}: ` + Moment(vehicle.state_updated).tz(this.customerContext.timezone()).format('lll zz')

          this.google.maps.event.addListener(marker, 'click', () => {
            infoWindow.close()
            infoWindow.setContent(infoWindowContent)
            infoWindow.open(map, marker)
          })

          markers.push(marker)
        })

        // eslint-disable-next-line no-new
        new MarkerClusterer(map, markers, {
          maxZoom: 13,
          minimumClusterSize: 4,
          gridSize: 35,
          averageCenter: true,
          styles: clusterStyles
        })

        map.fitBounds(bounds)
        map.panToBounds(bounds)
      })
    },
    /**
     * Retrieve active vehicles for this customer (updated less than 30 days ago)
     */
    fetchVehicles () {
      this.vehiclesLoaded = false
      const requestParams = {
        updated_after: '-45 days',
        fields: 'id,name,state,state_updated',
        'per-page': 0
      }
      if (!this.allCustomers) {
        requestParams.customer_id = this.customerContextId
      }
      return this.$orm('Vehicle').findAll(requestParams)
        .then(resp => {
          // Filter out any vehicles with missing or invalid coordinates
          this.vehicles = filter(resp.getItems(), vehicle => {
            return get(vehicle, 'state.lat.value', 0) !== 0 && get(vehicle, 'state.long.value', 0) !== 0
          })
          this.vehiclesLoaded = true
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      this.initialize()
    }
  },
  mounted () {
    this.initialize()
  },
  watch: {
    /** When we switch to viewing all customers and back, reload the map. */
    allCustomers (newValue, oldValue) {
      this.initialize()
    }
  }
}
</script>

<style scoped lang="scss">
#vehicle-map {
  height: calc(100% - 80px);
}
</style>
